import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  createPlugins,
  createBoldPlugin,
  createItalicPlugin,
  createUnderlinePlugin,
  createSoftBreakPlugin,
} from '@udecode/plate';

import AgentAssistanceConfigContext from 'components/contexts/agent_assistance_config_context';
import components from 'components/text_editor_new/plugins/components';
import createAITextCompletion from 'components/text_editor_new/plugins/ai/ai_text_completion';
import createAITextCompletionHighlight from 'components/text_editor_new/plugins/ai/ai_text_completion/ai_text_completion_highlight';
import { createAnswersPlugin } from 'components/text_editor_new/plugins/answers/answers_plugin';
import createCapitalizeFirstWordPlugin from 'components/text_editor_new/plugins/capitalize_first_word';
import createCapitalizeIPlugin from 'components/text_editor_new/plugins/capitalize_i';
import { createEditorEventsPlugin } from 'components/text_editor_new/plugins/editor_events/create_editor_events_plugin';
import { createHotkeyPlugin } from 'components/text_editor_new/plugins/hotkey/create_hotkey_plugin';
import createInsertTextAfterInline from 'components/text_editor_new/plugins/insert_text_after_inline';
import createParagraphPlugin from 'components/text_editor_new/plugins/paragraph';
import createPlaceholders from 'components/text_editor_new/plugins/placeholders';
import { createAutolinkPlugin } from 'components/text_editor_new/plugins/inline_links/create_autolink_plugin';
import { createLinkPlugin } from 'components/text_editor_new/plugins/inline_links/create_link_plugin';
import { createLinkSelectionPlugin } from 'components/text_editor_new/plugins/inline_links/create_link_selection_plugin';
import createSaveOnBlur from 'components/text_editor_new/plugins/save_on_blur';
import createSuggestedReplies from 'components/text_editor_new/plugins/suggested_replies/create_suggested_replies';
import { useCreateOpenAnswerPanelPlugin } from 'components/text_editor_new/plugins/create_open_answer_panel_plugin';
import { useCreateSubmitOnEnterPlugin } from 'components/text_editor_new/plugins/submit_on_enter';
import { useCreateVariablesPlugin, useUpdateVariables } from 'components/text_editor_new/plugins/variables';
import useCreateAutocompletePlugin from 'components/text_editor_new/plugins/autocomplete/use_create_autocomplete_plugin';
import useEditorWithDecorators from 'components/text_editor_new/hooks/use_editor_with_decorators';
import { useCreatePasteAttachmentsPlugin } from 'components/text_editor_new/plugins/create_paste_attachments_plugin';
import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';

// Chat supports only bold/italic/underline/link formatting. Loading a restricted
// plugin set (vs. useRichTextEditorPlugins) means Plate's HTML deserializer drops
// unsupported tags on paste — headings, lists, colors, font sizes, alignment,
// images — so pasted content is coerced to what the chat toolbar can produce.
export default function useChatRichTextEditorPlugins({ composerRef }, editorProps, extraPlugins) {
  const { name, onBlur, onSubmit, editorId } = editorProps;
  const isFeatureEnabled = useIsFeatureEnabled();
  const agentAssistanceConfig = useContext(AgentAssistanceConfigContext);
  const includeAIFeatures = !agentAssistanceConfig.disableHeroAI;

  // Lock in aiPlugins on first render to match useMessagingEditorPlugins. The
  // parent uses withRefreshEditorWhenFlagsChange to remount when the flag flips,
  // and Plate/Slate don't tolerate plugin lists changing after init.
  const [aiPlugins] = useState(() => {
    return includeAIFeatures ? [createAITextCompletion(), createAITextCompletionHighlight()] : null;
  });

  const createOpenAnswerPanelPlugin = useCreateOpenAnswerPanelPlugin();
  const createVariablesPlugin = useCreateVariablesPlugin();
  const createPasteAttachmentsPlugin = useCreatePasteAttachmentsPlugin();
  const createAutocompletePlugin = useCreateAutocompletePlugin();
  const createSubmitOnEnterPlugin = useCreateSubmitOnEnterPlugin(onSubmit);

  const isSuggestedRepliesEnabled = isFeatureEnabled('suggestedReplies');
  const [suggestedRepliesPlugin] = useState(() => {
    return isSuggestedRepliesEnabled ? createSuggestedReplies() : null;
  });

  const plugins = useMemo(() => {
    // ORDER MATTERS. Some plugins can interact with each other in adverse ways. Be careful when changing the order.
    let pluginList = [
      createInsertTextAfterInline(),

      createHotkeyPlugin(),
      createEditorEventsPlugin(),

      // Autocomplete must come before the autocapitalization plugins, as their onKeyDown handlers interfere with the
      // behavior of the autocomplete plugin.
      createAutocompletePlugin(),

      createCapitalizeIPlugin(),
      createCapitalizeFirstWordPlugin(),

      createSaveOnBlur(onBlur),
      createOpenAnswerPanelPlugin(),

      createBoldPlugin(),
      createItalicPlugin(),
      createUnderlinePlugin(),

      createParagraphPlugin(),

      createPlaceholders(),
      createLinkPlugin(),
      createAutolinkPlugin(),
      createLinkSelectionPlugin(),

      createAnswersPlugin(),
      createVariablesPlugin(),
      createPasteAttachmentsPlugin(),

      createSubmitOnEnterPlugin(),
      createSoftBreakPlugin(),
    ];

    if (aiPlugins && aiPlugins.length) {
      pluginList = [...pluginList, ...aiPlugins];
    }

    if (extraPlugins) {
      pluginList = [...pluginList, ...extraPlugins];
    }

    if (suggestedRepliesPlugin) {
      pluginList = pluginList.concat(suggestedRepliesPlugin);
    }

    return createPlugins(pluginList, { components });
  }, [
    aiPlugins,
    createAutocompletePlugin,
    createOpenAnswerPanelPlugin,
    createPasteAttachmentsPlugin,
    createSubmitOnEnterPlugin,
    createVariablesPlugin,
    extraPlugins,
    onBlur,
    suggestedRepliesPlugin,
  ]);

  const editor = useEditorWithDecorators(plugins, { editorId });
  useUpdateVariables(editor);

  const editorRef = useRef(editor);
  const [, rerender] = useState(null);

  useEffect(() => {
    if (!editorRef.current) {
      editorRef.current = editor;
      rerender({});
    }
    if (!composerRef.current.editorRefs[name]) {
      composerRef.current.editorRefs[name] = editorRef;
      rerender({});
    }
  }, [composerRef, editor, name]);
  editorProps.editorRef = editorRef;

  return editorRef;
}
